import React, { useEffect, useMemo } from 'react';
import { Box, Text, Flex } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { createColumnHelper } from '@tanstack/react-table';
import CommonTable from 'components/commonTable/CommonTable';
import { ListMerchantInfoData } from 'reactRedux/thunks/merchantInfoThunk/merchantInfoThunk';
import { sideBarRoutes, revenueOption } from 'utils/StaticData';

const columnHelper = createColumnHelper();

const MerchantInfo = () => {
  const dispatch = useDispatch();
  const { adminDetails } = useSelector((state) => state.authSlice);
  const { merchantInfoData, isLoading } = useSelector(
    (state) => state.MerchantInfoSlice,
  );

  // Check if user is a seller
  const isSeller = [
    sideBarRoutes?.Seller,
    sideBarRoutes?.Organization,
  ].includes(adminDetails?.role);

  // Function to get revenue option label by ID
  const getRevenueOptionLabel = (id) => {
    const option = revenueOption.find((option) => option.id === id);
    return option ? option.label : 'Unknown';
  };

  // Define table columns
  const columns = useMemo(
    () => [
      columnHelper.accessor('merchantName', {
        id: 'merchantName',
        header: () => (
          <Text
            justifyContent="space-between"
            align="center"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
          >
            MERCHANT
          </Text>
        ),
        cell: (info) => (
          <Text color="secondaryGray.900" fontSize="sm" fontWeight="700">
            {info.getValue()}
          </Text>
        ),
      }),
      columnHelper.accessor('commission', {
        id: 'commission',
        header: () => (
          <Text
            justifyContent="space-between"
            align="center"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
          >
            COMMISSION
          </Text>
        ),
        cell: (info) => (
          <Text color="secondaryGray.900" fontSize="sm" fontWeight="700">
            {info.getValue()}
          </Text>
        ),
      }),
      columnHelper.accessor('commissionOn', {
        id: 'commissionOn',
        header: () => (
          <Text
            justifyContent="space-between"
            align="center"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
          >
            COMMISSION ON
          </Text>
        ),
        cell: (info) => (
          <Text color="secondaryGray.900" fontSize="sm" fontWeight="700">
            {getRevenueOptionLabel(info.getValue())}
          </Text>
        ),
      }),
      columnHelper.accessor('definition', {
        id: 'definition',
        header: () => (
          <Text
            justifyContent="space-between"
            align="center"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
          >
            DEFINITION
          </Text>
        ),
        cell: (info) => {
          const definition = info.getValue();

          // Handle object format
          if (definition && typeof definition === 'object') {
            return (
              <Box>
                <Text
                  color="secondaryGray.900"
                  fontSize="sm"
                  fontWeight="600"
                  mb={1}
                >
                  Customer Purchases:{' '}
                  {definition.includeCustomerPurchases ? 'Yes' : 'No'}
                </Text>
                <Text
                  color="secondaryGray.900"
                  fontSize="sm"
                  fontWeight="600"
                  mb={1}
                >
                  Transaction Cost:{' '}
                  {definition.includeTransactionCost ? 'Yes' : 'No'}
                </Text>
                <Text color="secondaryGray.900" fontSize="sm" fontWeight="600">
                  Powerbank Cost:{' '}
                  {definition.includePowerbankCost ? 'Yes' : 'No'}
                </Text>
              </Box>
            );
          }

          // Fallback for null/undefined values
          return (
            <Text color="gray.400" fontSize="sm" fontStyle="italic">
              No definition available
            </Text>
          );
        },
      }),
    ],
    [],
  );

  // Fetch merchant info data on component mount
  useEffect(() => {
    if (isSeller) {
      dispatch(ListMerchantInfoData());
    }
  }, [dispatch, isSeller]);

  // If user is not a seller, don't render the component
  if (!isSeller) {
    return null;
  }

  return (
    <Box pt={{ base: '60px', md: '80px', xl: '78px' }}>
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="2xl" fontWeight="bold">
          Merchant Information
        </Text>
      </Flex>

      <CommonTable
        columns={columns}
        data={merchantInfoData || []}
        isLoading={isLoading}
        keyValue="merchantId"
        dynamicWidth={{
          merchantName: '250px',
          commission: '250px',
          commissionOn: '250px',
          definition: '250px',
        }}
      />
    </Box>
  );
};

export default MerchantInfo;
